import { Image, Skeleton, SkeletonizedValue } from '@round/ui-kit';
import { ColumnDef } from '@tanstack/react-table';
import styles from './SongsTable.module.css';
import { Badge, assets } from '@round/whitelabel-ui';
import { capitalize, uniq } from 'lodash';
import { numberWithCommas } from 'helpers';
import { Dropdown } from '@round/whitelabel-ui';
import { VirtualizedTable, VirtualizedTableProps } from '@round/whitelabel-ui';
import { TooltipOnOverflow } from '@round/whitelabel-ui';
import SongGrowthChart from '../../components/SongGrowthChart/SongGrowthChart';
import { Meta, SongsTableRow, TableCellContext, getTableMeta } from './helpers';

const { InstagramColor: InstagramIcon, TiktokColor: TikTokIcon, Edit: EditIcon, Trash: TrashIcon } = assets;

type Props = Pick<VirtualizedTableProps<SongsTableRow>, 'data' | 'onRowClick' | 'onEndReached'> & {
    meta: Meta;
};

const SongsTable = ({ data, onRowClick, onEndReached, meta }: Props) => {
    const columns: ColumnDef<SongsTableRow, any>[] = [
        {
            header: 'Song',
            id: 'song',
            cell: ({ row: { original }, table }) => {
                const { isLoading, isMusicSongLoading } = getTableMeta(table);
                const isSongLoading = isLoading || isMusicSongLoading(original.id);

                const title = original.musicSong?.title || original.title;
                const artists = original.musicSong?.artists.map((a) => a.name).join(', ') || original.artist_name;
                return (
                    <div className={styles.songContainer}>
                        <Image
                            skeletonCircle={false}
                            className={styles.image}
                            loading={isSongLoading}
                            src={original.musicSong?.image ?? ''}
                            alt={original.title}
                        />

                        <div className={styles.songTitles}>
                            <SkeletonizedValue width="10rem" isInitialized={!isSongLoading}>
                                <TooltipOnOverflow tooltip={title}>
                                    <p className={styles.songTitle}>{title}</p>
                                </TooltipOnOverflow>
                            </SkeletonizedValue>

                            <SkeletonizedValue width="7rem" isInitialized={!isSongLoading}>
                                <TooltipOnOverflow tooltip={artists}>
                                    <p className={styles.subValue}>{artists}</p>
                                </TooltipOnOverflow>
                            </SkeletonizedValue>
                        </div>
                    </div>
                );
            },
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: ({ getValue, table }: TableCellContext<'status'>) => {
                const { isLoading } = getTableMeta(table);

                if (isLoading) {
                    return <Skeleton width="10rem" />;
                }

                return <Badge className={styles[getValue()]}>{capitalize(getValue())}</Badge>;
            },
        },
        {
            header: 'Audios',
            id: 'audios',
            meta: {
                className: styles.audiosHeader,
            },
            columns: [
                {
                    header: () => <InstagramIcon className={styles.platformHeaderIcon} />,
                    accessorKey: 'instagram_audios',
                    meta: {
                        className: styles.audiosCell,
                    },
                    cell: ({ getValue, row: { original }, table }: TableCellContext<'instagram_audios'>) => {
                        const { isLoading, isMusicSongLoading } = getTableMeta(table);
                        const isSongLoading = isLoading || isMusicSongLoading(original.id);

                        if (isSongLoading) {
                            return <Skeleton width="1rem" />;
                        }

                        const audios = uniq((original.musicSong?.instagram_audios ?? []).concat(getValue()));
                        return <span>{numberWithCommas(audios.length)}</span>;
                    },
                },
                {
                    header: () => <TikTokIcon className={styles.platformHeaderIcon} />,
                    accessorKey: 'tiktok_audios',
                    meta: {
                        className: styles.audiosCell,
                    },
                    cell: ({ getValue, row: { original }, table }: TableCellContext<'tiktok_audios'>) => {
                        const { isLoading, isMusicSongLoading } = getTableMeta(table);
                        const isSongLoading = isLoading || isMusicSongLoading(original.id);

                        if (isSongLoading) {
                            return <Skeleton width="1rem" />;
                        }

                        const audios = uniq((original.musicSong?.tiktok_audios ?? []).concat(getValue()));
                        return <span>{numberWithCommas(audios.length)}</span>;
                    },
                },
            ],
        },
        {
            header: '',
            id: 'dailyChange',
            accessorKey: 'timeSeries',
            meta: {
                className: styles.dailyChange,
            },
            cell: ({ getValue, row, table }: TableCellContext<'timeSeries'>) => {
                const { isLoading, isTimeSeriesLoading, isTimeSeriesError } = getTableMeta(table);
                if (isLoading) {
                    return <Skeleton width="10rem" />;
                }

                return (
                    <SongGrowthChart
                        timeSeries={getValue()}
                        isLoading={isTimeSeriesLoading(row.original.id)}
                        hasError={isTimeSeriesError(row.original.id)}
                    />
                );
            },
        },
        {
            header: '',
            id: 'actions',
            meta: {
                className: styles.actions,
            },
            cell: ({ row: { original }, table }) => {
                const { isLoading, onEditSong, onDeleteSong } = getTableMeta(table);

                if (isLoading) {
                    return <Skeleton width="1rem" />;
                }

                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            icon={EditIcon}
                                            onClick={() => {
                                                onEditSong(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Edit song
                                        </Dropdown.Item>

                                        <Dropdown.Separator />

                                        <Dropdown.Item
                                            icon={() => <TrashIcon className={styles.deleteAction} />}
                                            onClick={() => {
                                                onDeleteSong(original);
                                                setIsOpen(false);
                                            }}
                                            className={styles.deleteAction}
                                        >
                                            Delete song
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const emptyRows = new Array(10).fill({}) as SongsTableRow[];
    const rows = meta.isLoading ? data.concat(emptyRows) : data;

    return (
        <VirtualizedTable
            isLoading={meta.isLoading}
            containerClassName={styles.tableContainer}
            className={styles.table}
            data={rows}
            columns={columns}
            meta={meta}
            noDataLabel={
                <div aria-label="no songs" className={styles.noDataContainer}>
                    {meta.hasError ? "Couldn't fetch songs" : 'No songs found'}
                </div>
            }
            onRowClick={onRowClick}
            onEndReached={onEndReached}
            virtualizerOptions={{
                count: rows.length,
                estimateSize: () => 50,
                getItemKey: (index) => rows[index]?.id || index,
                overscan: 5,
            }}
        />
    );
};

export default SongsTable;
